// src/utils/branding.js
import SOURCE_MAP from "../config/sourceMap";

/**
 * Find the best source key for the current hostname.
 * Returns a source key (e.g. "wayuvega" or "jumbomail").
 */
export function detectSourceFromHostname(hostname = window.location.hostname) {
  if (!hostname) return "wayuvega";
  const host = hostname.toLowerCase();

  // Look through SOURCE_MAP and their "domains" arrays
  for (const [key, value] of Object.entries(SOURCE_MAP)) {
    if (Array.isArray(value.domains)) {
      for (const d of value.domains) {
        if (!d) continue;
        // exact match or ending match (e.g. allow subdomains)
        if (host === d.toLowerCase() || host.endsWith(`.${d.toLowerCase()}`)) {
          return key;
        }
      }
    }
  }

  // fallback to env or wayuvega
  return window.__DEFAULT_SOURCE__ || "wayuvega";
}

/**
 * Apply brand assets & some CSS variables to the document head/root.
 * Also stores the chosen source in localStorage so Header/Sidebar pick it up.
 */
export function applyBrandToDOM(sourceKey = "wayuvega") {
  const brand = SOURCE_MAP[sourceKey] || SOURCE_MAP.wayuvega;

  try {
    localStorage.setItem("source", sourceKey);
  } catch (e) {}

  // Document title
  if (brand.pageTitle) document.title = brand.pageTitle;

  // Favicon / apple touch icon / manifest
  const setOrCreateLink = (rel, href, attributes = {}) => {
    if (!href) return;
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", rel);
      document.head.appendChild(el);
    }
    el.setAttribute("href", href);
    Object.entries(attributes).forEach(([k, v]) => el.setAttribute(k, v));
  };

  setOrCreateLink("icon", brand.favicon);
  setOrCreateLink("shortcut icon", brand.favicon);
  setOrCreateLink("apple-touch-icon", brand.appleTouchIcon || brand.favicon, { sizes: "180x180" });

  // manifest
  if (brand.manifest) {
    let manifestEl = document.querySelector('link[rel="manifest"]');
    if (!manifestEl) {
      manifestEl = document.createElement("link");
      manifestEl.setAttribute("rel", "manifest");
      document.head.appendChild(manifestEl);
    }
    manifestEl.setAttribute("href", brand.manifest);
  }

  // theme-color meta
  if (brand.themeColor) {
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "theme-color");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", brand.themeColor);
  }

  // CSS variables on :root so components using var(...) pick them up
  const root = document.documentElement;
  root.style.setProperty("--brand-card-bg", brand.cardBackground || "#ffffff");
  // ensure --brand-text contains a color value (no tailwind classes)
  root.style.setProperty("--brand-text", brand.textColorValue || "#1e40af");
  root.style.setProperty("--brand-accent", brand.accentColor || "#3b82f6");
  root.style.setProperty("--brand-fg", brand.textColorValue || "#1e40af");
}
